import { useEffect, useState } from 'react';
import { Card, Button, Select, Input, Badge, DataTable, Column, NameCell, LongText, Textarea, Modal, useToast } from '../../../shared/components';
import { apiRequest } from '../../../shared/services/http';
import { getAuthToken } from '../../../shared/auth/tokenProvider';
import { useCurrentUser } from '../../../shared/auth/AuthProvider';
import { Overtime, OVERTIME_STATUS_LABEL, fmtMinutes } from '../../../shared/services/turnoGeo/types';

/**
 * Horas extras que TurnoGeo envio a Recursos Humanos: el empleado las confirmo y un jefe las autorizo.
 * Aqui Recursos Humanos las revisa y las marca como procesadas (liquidadas).
 */
export function TurnoGeoOvertimeTab() {
  const toast = useToast();
  const user = useCurrentUser();
  const canProcess = user.role === 'admin' || user.role === 'supervisor';
  const [status, setStatus] = useState('ENVIADA');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [rows, setRows] = useState<Overtime[] | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setRows(null);
    setSelected([]);
    try {
      const qs = new URLSearchParams();
      if (status) qs.set('status', status);
      if (dateFrom) qs.set('dateFrom', dateFrom);
      if (dateTo) qs.set('dateTo', dateTo);
      setRows(await apiRequest(`/api/hr/turnogeo-overtime?${qs.toString()}`, { token: await getAuthToken() }));
    } catch (e) { setRows([]); toast.show(e instanceof Error ? e.message : 'No se pudo cargar.', 'error'); }
  };
  useEffect(() => { void load(); }, [status, dateFrom, dateTo]);

  const process = async () => {
    setBusy(true);
    try {
      const r: { processed: number } = await apiRequest('/api/hr/turnogeo-overtime/process', { method: 'POST', body: { ids: selected, note: note.trim() || undefined }, token: await getAuthToken() });
      toast.show(`${r.processed} solicitud(es) marcada(s) como procesadas.`);
      setOpen(false); setNote('');
      await load();
    } catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo procesar.', 'error'); }
    finally { setBusy(false); }
  };

  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const columns: Column<Overtime>[] = [
    { key: 'user', header: 'Empleado', filterValue: (r) => r.userFullName, sortable: true, sortValue: (r) => r.userFullName, render: (r) => <NameCell name={r.userFullName} /> },
    { key: 'doc', header: 'CC', render: (r) => r.documentNumber ?? '—' },
    { key: 'date', header: 'Día', filterValue: (r) => r.workDate, sortable: true, sortValue: (r) => r.workDate, render: (r) => r.workDate },
    { key: 'appr', header: 'Horas autorizadas', render: (r) => fmtMinutes(r.approvedMinutes ?? 0) },
    { key: 'status', header: 'Estado', filterValue: (r) => OVERTIME_STATUS_LABEL[r.status], render: (r) => <Badge tone={r.status === 'PROCESADA' ? 'success' : 'info'}>{OVERTIME_STATUS_LABEL[r.status]}</Badge> },
    { key: 'by', header: 'Autorizó', render: (r) => r.reviewedBy ?? '—' },
    { key: 'sent', header: 'Enviada', render: (r) => (r.sentToHrDate ? `${r.sentToHrDate.slice(0, 10)} · ${r.sentToHrBy ?? ''}` : '—') },
    { key: 'note', header: 'Motivo del empleado', render: (r) => <LongText text={r.employeeNote} max={40} /> },
    { key: 'hr', header: 'Procesó', render: (r) => (r.hrProcessedBy ? `${r.hrProcessedBy}${r.hrNote ? ` · ${r.hrNote}` : ''}` : '—') },
    ...(canProcess ? [{ key: 'pick', header: '', render: (r: Overtime) => (r.status === 'ENVIADA' ? <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} aria-label="Seleccionar" /> : null) } as Column<Overtime>] : []),
  ];

  return (
    <div className="space-y-4">
      <Card>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Select label="Ver" value={status} onChange={(e) => setStatus(e.target.value)} options={[{ value: 'ENVIADA', label: 'Por procesar' }, { value: 'PROCESADA', label: 'Procesadas' }, { value: '', label: 'Todas' }]} />
            <Input label="Desde" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <Input label="Hasta" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          {canProcess && (
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => setOpen(true)} disabled={selected.length === 0}>Marcar como procesadas ({selected.length})</Button>
              <p className="text-xs text-ink-400">Estas horas ya fueron confirmadas por el empleado y autorizadas por su jefe en TurnoGeo.</p>
            </div>
          )}
        </div>
      </Card>
      <DataTable excel fixedKey="user" columns={columns} rows={rows ?? []} rowKey={(r) => r.id} isLoading={rows === null} emptyTitle="Sin horas de TurnoGeo" emptyDescription="Aquí llegan las horas extras que los jefes envían desde TurnoGeo." defaultSortKey="date" />
      <Modal open={open} onClose={() => setOpen(false)} title="Marcar como procesadas"
        footer={<div className="flex gap-2"><Button variant="outline" fullWidth onClick={() => setOpen(false)} disabled={busy}>Cancelar</Button><Button fullWidth onClick={process} isLoading={busy}>Confirmar</Button></div>}>
        <div className="space-y-3 text-sm text-ink-700">
          <p>Se marcarán {selected.length} solicitud(es) como procesadas por Recursos Humanos.</p>
          <Textarea label="Nota (opcional)" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej: liquidadas en nómina de octubre" />
        </div>
      </Modal>
    </div>
  );
}
